import _ from 'lodash';

import { Position } from 'src/app/shared/models/team/position';
import { Community } from 'src/app/shared/models/team/community';
import { ITeam } from 'src/app/shared/models/team/iteam';
import { TeamWrite } from 'src/app/shared/models/team/teamWrite';
import { PositionWrite } from 'src/app/shared/models/team/positionWrite';
import { Person } from 'src/app/people/models/person';
import { IamPersonOrTeam } from 'src/app/people/models/IHaveAttributes';
import { DateRange } from 'src/app/shared/models/dateRange';
import { SeatStatus } from 'src/app/shared/models/team/seatStatus';
import { EmailCustomization, EmailScenario } from 'src/app/shared/models/emailCustomization';
import { Attribute } from 'src/app/shared/models/attributes/attribute';
import { Seat } from './seat';
import { AttributeDefinition } from 'src/app/shared/models/attributes/attributeDefinition';
import { AttributeWrite } from 'src/app/shared/models/attributes/attributeWrite';
import { TeamStatusCount } from './teamStatusCount';
import { DemographicInfo } from 'src/app/people/models/demographicInfo';
import { isDateAfterOrEqualToday } from '../../utils/date-utils';

export class Team implements ITeam, IamPersonOrTeam {
  public id: string;
  public name: string;
  public publicName: string;
  public description: string;
  public isArchived: boolean;
  public archiveDate: Date;
  public createdOn: Date;
  public createdByFullName: string;
  public updatedOn: Date;
  public status: string;
  public community: Community;
  public dateRange: DateRange;
  public positions: Position[] = new Array<Position>();
  public emailMetadata: EmailCustomization[] = new Array<EmailCustomization>();
  public seatAttributes: AttributeDefinition[] = new Array<AttributeDefinition>();
  public attributes: Attribute[] = new Array<Attribute>();
  public statusCounts: TeamStatusCount[] = new Array<TeamStatusCount>();
  public workspaceId: string;
  public startDate: Date;
  public endDate: Date;
  public newSeatIdInPickProcess: string;
  public seatsChanged: string[] = new Array<string>();
  public seatsNotChanged: string[] = new Array<string>();
  public metadata: metadata;
  public validationMessages: string[] = new Array<string>();
  public bulkPickPeopleCount: number;
  public demographicInfo: DemographicInfo = null;

  constructor(
    id: string,
    name: string,
    publicName: string,
    description: string,
    isArchived: boolean,
    archivedDate: Date,
    createdOn: Date,
    createdByFullName: string,
    updatedOn: Date,
    status: string,
    community: Community,
    positions?: Position[],
    startDate?: Date,
    endDate?: Date,
    dateRange?: DateRange,
    emailCustomizations: EmailCustomization[] = null,
    seatAttributes: AttributeDefinition[] = null,
    attributes: Attribute[] = null,
    refreshStatusCounts: boolean = true,
    workspaceId: string = null,
    metadata: metadata = null,
    demographicInfo: DemographicInfo = null
  ) {
    this.id = id;
    this.name = name;
    this.publicName = publicName;
    this.description = description;
    this.isArchived = isArchived;
    this.archiveDate = archivedDate;
    this.createdOn = createdOn;
    this.createdByFullName = createdByFullName;
    this.updatedOn = updatedOn;
    this.status = status;
    this.community = community;
    this.positions = positions || Array<Position>();
    this.dateRange = dateRange ? dateRange : new DateRange(startDate, endDate);
    this.startDate = startDate;
    this.endDate = endDate;
    this.emailMetadata = emailCustomizations || Array<EmailCustomization>();
    this.seatAttributes =
      seatAttributes?.filter((x) => !x.isArchived) || Array<AttributeDefinition>();
    this.attributes = attributes || Array<Attribute>();
    if (refreshStatusCounts) this.refreshStatusCounts();
    this.workspaceId = workspaceId;
    this.metadata = metadata;
    this.demographicInfo = demographicInfo;
  }

  get seatCount() {
    return _.sumBy(this.positions, 'seatCount');
  }

  get draftCount() {
    return _.sumBy(this.positions, 'draftCount');
  }

  get hasPeopleOnSeats(): boolean {
    let excludeDeclined: boolean = true;
    return !_.isEmpty(this.getPeopleOnSeats(excludeDeclined));
  }

  public toTeamWrite(clone: boolean = false): TeamWrite {
    let positionWrites = new Array<PositionWrite>();
    let attributeWrites = AttributeWrite.fromDisparateAttributes(this.attributes);
    this.positions.forEach((p) => positionWrites.push(p.toPositionWrite(clone)));

    return new TeamWrite(
      clone ? null : this.id,
      clone ? `${this.name} (copy)` : this.name,
      this.publicName,
      positionWrites,
      attributeWrites,
      this.dateRange.startDate,
      this.dateRange.endDate
    );
  }

  public isFirstPosition(position: Position) {
    return position.ranking === _.minBy(this.positions, 'ranking').ranking;
  }

  public isLastPosition(position: Position) {
    return position.ranking === _.maxBy(this.positions, 'ranking').ranking;
  }

  public updatePerson(person: Person) {
    this.positions.forEach(function (position) {
      position.seats.forEach(function (seat) {
        if (seat.person && seat.person.id == person.id) {
          seat.person = person;
        }
      });
      position.reOrderSeats();
    });
  }

  public updateSeatAttribute(
    updatedSeat: Seat,
    attributeDefinitionId: string,
    updatedAttributes: Attribute[]
  ) {
    this.positions.forEach(function (position) {
      position.seats.forEach(function (seat) {
        if (seat.id === updatedSeat.id) {
          seat.updateAttribute(attributeDefinitionId, updatedAttributes);
        }
      });
      position.reOrderSeats();
    });
  }

  public deletePerson(person: Person) {
    this.positions.forEach(function (position) {
      var removed = _.remove(position.seats, function (seat) {
        return seat.person && seat.person.id == person.id;
      });
      if (removed.length) {
        position.reOrderSeats();
      }
    });
  }

  public personIsPickedOnTeam(person: Person) {
    return person.isPicked(this.positions);
  }

  public personIsInvitedOnTeam(person: Person) {
    return person.isInvited(this.positions);
  }

  public personIsNoResponseOnTeam(person: Person) {
    return person.isNoResponse(this.positions);
  }

  public personIsAcceptedOnTeam(person: Person) {
    return person.isAccepted(this.positions);
  }

  public personIsNotOnTeam(person: Person) {
    return !person.isPickedOrInvitedOrAcceptedOrDeclined(this.positions);
  }

  public getPeopleOnSeats(excludeDeclined: boolean = false, excludePast: boolean = false) {
    var peopleOnSeats = new Array<Person>();

    this.positions.forEach((position) => {
      position.seats.forEach((seat) => {
        if (seat.person) {
          if (excludeDeclined) {
            if (seat.status == SeatStatus.Declined || seat.status == SeatStatus.NoShow) {
              return;
            }
          }

          if (excludePast) {
            if (seat.endDate && !isDateAfterOrEqualToday(seat.endDate)) {
              return;
            }
          }

          let person = seat.person;
          if (person) {
            person.seatId = seat.id;
            peopleOnSeats.push(person);
          }
        }
      });
    });

    return peopleOnSeats;
  }

  public getPickedCount() {
    let picked = [];

    _.each(this.positions, function (position: Position) {
      _.each(position.seats, function (seat) {
        if (seat.status == SeatStatus.Picked || seat.status == SeatStatus.NoResponse) {
          picked.push(seat);
        }
      });
    });

    return picked.length;
  }

  public getInvitedCount() {
    let invited = [];

    this.positions.forEach((position) => {
      position.seats.forEach((seat) => {
        if (seat.status == SeatStatus.Invited) {
          invited.push(seat);
        }
      });
    });

    return invited.length;
  }

  /** does a selectMany of positions.seats. sorts by seat.person.fullName */
  public getAllSeats(excludeDeclined: boolean = true): Array<Seat> {
    var seats = _.chain(this.positions)
      .map((p) => p.seats)
      .flatten();

    if (excludeDeclined) {
      seats = seats.filter((s) => s.status != SeatStatus.Declined && s.status != SeatStatus.NoShow);
    }

    return seats.orderBy((s) => s.person.fullName).value();
  }

  public updatePosition(position: Position): boolean {
    if (!position) return false;
    let positionIndex = this.positions.findIndex((p) => p.id == position.id);
    let foundPosition = positionIndex > -1;

    if (foundPosition) {
      this.positions[positionIndex] = position;
    }
    this.refreshStatusCounts();

    return true;
  }

  public addPosition(position: Position): boolean {
    if (!position) return false;
    let positionIndex = this.positions.findIndex((p) => p.id == position.id);
    let foundPosition = positionIndex > -1;

    if (foundPosition) {
      this.positions[positionIndex] = position;
    } else {
      this.positions.push(position);
    }
    this.refreshStatusCounts();

    return true;
  }

  public getPositionTitle(positionId: string): string {
    return this.positions.find((p) => p.id == positionId).title;
  }

  public removePosition(position: Position): boolean {
    if (!position) return false;
    let positionIndex = this.positions.findIndex((p) => p.id == position.id);
    let foundPosition = positionIndex > -1;

    if (foundPosition) {
      this.positions.splice(positionIndex, 1);
      this.updatePositionRankings();
      this.refreshStatusCounts();
    }

    return foundPosition;
  }

  public updatePositionRankings() {
    for (var i = 0; i < this.positions.length; i++) {
      this.positions[i].ranking = i + 1;
    }
  }

  public invitationEmailCustomization(scenario: EmailScenario): EmailCustomization {
    return _.chain(this.emailMetadata)
      .filter((ec) => ec.scenario === scenario)
      .first()
      .value();
  }

  public hasInvitationEmailCustomization(scenario: EmailScenario): boolean {
    let customDefault = this.invitationEmailCustomization(scenario);
    return customDefault != undefined && customDefault.isCustom;
  }

  public updateEmailCustomization(emailCustomization: EmailCustomization) {
    var customization = EmailCustomization.parse(emailCustomization);
    _.remove(this.emailMetadata, (ec) => ec.scenario === emailCustomization.scenario);
    this.emailMetadata.push(customization);
    return emailCustomization;
  }

  public descriptionWithoutHtml() {
    let replacedTags = this.description.replace(/<[^>]*>/g, ' ');
    let replacedSpaces = replacedTags.replace(/&nbsp;/g, ' ');
    return replacedSpaces;
  }

  public hasSeatAttributes(): boolean {
    return !_.isNil(this.seatAttributes) && this.seatAttributes.length > 0;
  }

  public hasSeatAttribute(seatAttribute: AttributeDefinition): boolean {
    return !_.isNil(_.find(this.seatAttributes, { id: seatAttribute.id }));
  }

  public getSeatAttributeDifference(seatAttributes: AttributeDefinition[]): AttributeDefinition[] {
    return _.differenceBy(this.seatAttributes, seatAttributes, 'id');
  }

  hasDeclinedPositionsByPerson(personId: string) {
    let result = this.positions
      .map((p) =>
        p.seats.findIndex(
          (s) =>
            s.person.id == personId &&
            (s.status == SeatStatus.Declined || s.status == SeatStatus.NoShow)
        )
      )
      .filter((x) => x > -1);
    return result.length > 0;
  }

  hasCommunity() {
    return this.community ? true : false;
  }

  public personCount(personId: string) {
    return this.positions.reduce((personCountinPosition, curr) => {
      let perCountinSeat = curr.seats.filter((session) => session.person?.id == personId).length;
      return personCountinPosition + perCountinSeat;
    }, 0);
  }

  public refreshStatusCounts() {
    let seatCounts = {
      picked: 0,
      accepted: 0,
      declined: 0,
      noShow: 0,
      invited: 0,
      noResponse: 0,
      filled: 0,
      empty: 0
    };

    var positionCounts = _.chain(this.positions)
      .flatMap((position) => position.statusCounts)
      .value();

    _.forEach(positionCounts, (count) => {
      switch (count.status) {
        case SeatStatus.Accepted:
          seatCounts.accepted += count.count;
          break;
        case SeatStatus.Declined:
          seatCounts.declined += count.count;
          break;
        case SeatStatus.NoShow:
          seatCounts.noShow += count.count;
          break;
        case SeatStatus.Picked:
          seatCounts.picked += count.count;
          break;
        case SeatStatus.Invited:
          seatCounts.invited += count.count;
          break;
        case SeatStatus.NoResponse:
          seatCounts.noResponse += count.count;
          break;
        default:
          seatCounts.empty += count.count;
      }
    });

    let teamCounts = new Array<TeamStatusCount>();
    teamCounts.push(new TeamStatusCount(SeatStatus.Picked, seatCounts.picked));
    teamCounts.push(new TeamStatusCount(SeatStatus.Invited, seatCounts.invited));
    teamCounts.push(new TeamStatusCount(SeatStatus.NoResponse, seatCounts.noResponse));
    teamCounts.push(new TeamStatusCount(SeatStatus.Declined, seatCounts.declined));
    teamCounts.push(new TeamStatusCount(SeatStatus.NoShow, seatCounts.noShow));
    teamCounts.push(new TeamStatusCount(SeatStatus.Accepted, seatCounts.accepted));
    teamCounts.push(new TeamStatusCount(null, seatCounts.empty));

    this.statusCounts = _.sortBy(teamCounts, (count) => count.rank);
  }

  private setStatusCounts(teamCounts: TeamStatusCount[]) {
    this.statusCounts = _.sortBy(teamCounts, (count) => count.rank);
  }

  static parseFromSummary(obj: any): Team {
    let team = Team.parse(obj, false);

    let counts = new Array<TeamStatusCount>();
    counts.push(new TeamStatusCount(SeatStatus.Picked, obj.picked));
    counts.push(new TeamStatusCount(SeatStatus.Invited, obj.invited));
    counts.push(new TeamStatusCount(SeatStatus.NoResponse, obj.noResponse));
    counts.push(new TeamStatusCount(SeatStatus.Declined, obj.declined));
    counts.push(new TeamStatusCount(SeatStatus.NoShow, obj.noShow));
    counts.push(new TeamStatusCount(SeatStatus.Accepted, obj.accepted));
    counts.push(new TeamStatusCount(null, obj.empty));
    team.setStatusCounts(counts);

    return team;
  }

  static parseSearchQuery(obj: any): Team {
    let team = Team.parse(obj, false);

    let counts = new Array<TeamStatusCount>();
    counts.push(new TeamStatusCount(SeatStatus.Picked, obj.summary.picked));
    counts.push(new TeamStatusCount(SeatStatus.Invited, obj.summary.invited));
    counts.push(new TeamStatusCount(SeatStatus.NoResponse, obj.summary.noResponse));
    counts.push(new TeamStatusCount(SeatStatus.Declined, obj.summary.declined));
    counts.push(new TeamStatusCount(SeatStatus.NoShow, obj.summary.noShow));
    counts.push(new TeamStatusCount(SeatStatus.Accepted, obj.summary.accepted));
    counts.push(new TeamStatusCount(null, obj.summary.empty));
    team.setStatusCounts(counts);

    return team;
  }

  static parse(obj: any, refreshStatusCounts: boolean = true): Team {
    if (!obj) return;
    let positions = new Array<Position>();
    let seatAttributes = new Array<AttributeDefinition>();

    if (obj.positions) {
      obj.positions.forEach((p) => {
        positions.push(Position.parse(p));
      });
    }

    if (obj.seatAttributes) {
      obj.seatAttributes.forEach((attributeDefinition) => {
        seatAttributes.push(AttributeDefinition.parse(attributeDefinition));
      });
    }

    return new Team(
      obj.id,
      obj.name,
      obj.publicName,
      obj.description,
      obj.isArchived,
      obj.archiveDate,
      obj.createdOn,
      obj.createdByFullName,
      obj.updatedOn,
      obj.status,
      obj.community,
      _.sortBy(positions, (p) => p.ranking),
      obj.startDate,
      obj.endDate,
      obj.dateRange,
      obj.emailMetadata ? EmailCustomization.parseMany(obj.emailMetadata) : [],
      _.sortBy(seatAttributes, 'name'),
      _.orderBy(
        _.map(obj.attributes, (a) => Attribute.parse(a)),
        'name'
      ),
      refreshStatusCounts,
      obj.workspaceId,
      obj.metadata,
      obj.demographicInfo
    );
  }
}

interface metadata {
  externalSystemId: string;
}

export enum TeamToggleValue {
  Roster = 'roster',
  AllSeats = 'allSeats',
  BenchSeats = 'benchSeats'
}
